import IconInfo from '@apollo/icons/default/IconInfo.svg';
import React from 'react';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorInsightsBreakdownItemFragment } from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

export const SampleMessageHeader = () => (
  <div className="flex items-center">
    <span className="truncate" title="Sample message">
      Sample message
    </span>
    <Tooltip
      className="whitespace-normal"
      label={
        <>
          <p>
            A sample error message for this category. Additional errors can be
            explored by viewing operations and traces.
          </p>
          <p className="mt-2">
            Note: Messages may not be available for some errors, either due to
            the nature of the error, trace sampling, or error reporting
            configuration.
            {/* FIXME: Learn more about{' '}
            <Link isExternal to={DocsRouter.path('ErrorConfiguration')}>
              reporting errors
            </Link>{' '}
            in the docs. */}
          </p>
        </>
      }
      hasArrow
      placement="top"
      gutter={20}
      // interactive FIXME: setting this to interactive causes the tooltip to
      // be clipped by the accordion header.
    >
      <IconInfo className="icon-secondary ml-2" />
    </Tooltip>
  </div>
);

/**
 * Given some text, smartly break words using `<wbr />` elements
 * according to the special "break" characters defined by `breakPattern`.
 * @param text The text to break.
 * @param breakPattern A RegExp which matches the special characters to break on.
 * Defaults to breaking on non-alpha-numeric and non-whitespace characters
 * (whitespace characters are broken by the browser automatically).
 * @returns A ReactNode containing the split array of words or
 * the original string if no special characters found.
 */
function breakWordsOnSpecialCharacters(
  text: string,
  breakPattern: RegExp = /[^a-zA-Z\d\s]/g,
): React.ReactNode {
  const splitText = text.split(breakPattern);

  if (splitText.length <= 1) {
    return text;
  }

  const matches = text.match(breakPattern);

  return splitText.reduce<React.ReactNode[]>(
    (arr, element, index) =>
      matches && matches[index]
        ? // eslint-disable-next-line react/no-array-index-key
          [...arr, element, matches[index], <wbr key={index} />]
        : [...arr, element],
    [],
  );
}

export const SampleMessage = ({
  traceRefs,
}: {
  traceRefs: ErrorInsightsBreakdownItemFragment['traceRefs'];
}) => {
  const errorMessage =
    traceRefs &&
    isMinLengthArray(1, traceRefs.items) &&
    traceRefs.items[0].errorMessage;
  return (
    <p className="max-h-24 overflow-y-auto overflow-x-hidden font-mono">
      {errorMessage ? (
        <code className="text-sm text-secondary" title={errorMessage}>
          {breakWordsOnSpecialCharacters(errorMessage)}
        </code>
      ) : (
        <span className="italic text-disabled">-</span>
      )}
    </p>
  );
};
